import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
    usePeriod,
    useProductCredit,
    useProducts,
    useTranslate,
    authenticationState,
    countryState,
    customerState,
    languageState,
    productsState
} from '@repo/unicity'
import { spotlightCustomerHelpers as customerHelpers } from '../../helpers/customerCompat'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Payment from '../creditcard/Payment'
import { addressApis } from '../address/Apis'
import { orderDetailState } from '../store/orderDetail'
import InputBox from '../forms/inputbox/InputBox'
import ErrorBox from '../errorBox/ErrorBox'
import { isReadyState, loadUuidState } from '../store/loadingState'
// import MoneyBack from '../moneyback/MoneyBack'
import { orderDetailsApis } from './Apis'
import { feelgreatState } from '../store/feelgreat'
import { useNavigate } from 'react-router'
import { displayEmailState, editedEmailState, editedPhoneFormatState, editedPhoneState } from '../store/formNewAddress'
import Cart from '../cart/Cart'
import { useAddressHistory } from '../core/useAddressHistory.ts/useAddressHistory'
import NewAddress from '../address/NewAddress'
import { isEmpty } from 'lodash'
import { useQueryParams } from '../../hooks/useQueryParams'

// Hoisted regex for phone number formatting
const NON_DIGIT_REGEX = /\D/g
const PHONE_REGEX_11 = /(\d{3})(\d{3})(\d{5})/
const PHONE_REGEX_10 = /(\d{3})(\d{3})(\d{4})/

// Shared responsive styles to avoid duplication
const responsiveMainContainerSx = {
    '@media (max-width: 767px)': { maxWidth: '100%' },
    '@media (min-width: 768px)': { maxWidth: '600px' },
    '@media (min-width: 1024px)': { maxWidth: '300px' },
    '@media (min-width: 1180px)': { maxWidth: '390px' },
    '@media (min-width: 1200px)': { maxWidth: '490px' }
}

const responsiveSecondaryContainerSx = {
    boxSizing: 'border-box',
    '@media (max-width: 768px)': { maxWidth: '100%' },
    '@media (min-width: 768px)': { maxWidth: '600px' },
    '@media (min-width: 1180px)': { maxWidth: '672px' },
    '@media (min-width: 1200px)': { maxWidth: '772px' }
}

const responsiveSkeletonMainSx = {
    '@media (orientation: portrait)': {
        '@media (max-width: 767px)': { maxWidth: '100%' },
        '@media (min-width: 768px)': { maxWidth: '600px' }
    },
    '@media (orientation: landscape)': {
        '@media (min-width: 1024px)': { maxWidth: '390px' },
        '@media (min-width: 1180px)': { maxWidth: '390px' },
        '@media (min-width: 1200px)': { maxWidth: '490px' }
    },
    height: '300px',
    borderRadius: '12px'
}

const responsiveSkeletonSecondarySx = {
    '@media (max-width: 768px)': { maxWidth: '100%' },
    '@media (min-width: 768px)': { maxWidth: '600px' },
    '@media (min-width: 1180px)': { maxWidth: '672px' },
    '@media (min-width: 1200px)': { maxWidth: '772px' },
    borderRadius: '12px'
}

const OrderDetails = () => {
    const [isReady, setIsReady] = useAtom(isReadyState)
    const qs = useQueryParams()
    const uuid = qs.get('uuid')
    const navigate = useNavigate()
    const { translate } = useTranslate()
    const customer = useAtomValue(customerState)
    const country = useAtomValue(countryState)
    const auth = useAtomValue(authenticationState)

    const [orderDetail, setOrderDetail] = useAtom(orderDetailState)
    const page = /payment/.test(window.location.pathname) ? 'payment' : 'address'
    const [shouldAddNewAddress, setShouldAddNewAddress] = useState(false)
    const [isEdit, setIsEdit] = useState(false)

    const [editedPhone, setEditedPhone] = useAtom(editedPhoneState)
    const [editedEmail, setEditedEmail] = useAtom(editedEmailState)
    const [phoneFormat, setPhoneFormat] = useAtom(editedPhoneFormatState)
    const [displayEmail, setDisplayEmail] = useAtom(displayEmailState)
    const [noEmail, setNoEmail] = useState(false)
    const [noPhone, setNoPhone] = useState(false)

    const [error, setError] = useState<string[]>([])
    const [editContactError, setEditContactError] = useState<string[]>([])

    const [trigger, setTrigger] = useState(new Date().getTime())
    const [loading, setLoading] = useState(false)

    const [loadingUuid, setLoadingUuid] = useAtom(loadUuidState)

    const setFeelgreat = useSetAtom(feelgreatState)
    const language = useAtomValue(languageState)

    const { getPeriod } = usePeriod()
    const { productCredits, fetchProductCredits } = useProductCredit({
        baId: customer.id.unicity,
        ushopCountryCode: country.short,
        token: auth.token as string
    })
    const { getProducts, isPending: isProductsPending } = useProducts({ allow: 'spotlight', status: 'A' })
    const products = useAtomValue(productsState)
    const [isProductCreditLoaded, setIsProductCreditLoaded] = useState(false)
    const [isProductLoaded, setIsProductLoaded] = useState(false)

    // Derived state - no need for useEffect + useState
    const forceEnterRequiredData = useMemo(() => !isReady && (noEmail || noPhone), [isReady, noEmail, noPhone])

    useEffect(() => {
        getPeriod()
        fetchProductCredits().then(() => setIsProductCreditLoaded(true))
        getProducts()
    }, [])

    useEffect(() => {
        if (!isProductsPending && products.length > 0) {
            setIsProductLoaded(true)
        }
    }, [isProductsPending, products.length])

    const { isFetched, addressHistory, isAllowed, getAddressHistory } = useAddressHistory({
        baId: customer.id.unicity,
        token: auth.token as string,
        userCountry: customer.mainAddress.country,
        webCountry: country.short
    })

    const formatPhoneNumber = useCallback((num: string) => {
        const digits = String(num).replace(NON_DIGIT_REGEX, '')
        if (digits.length === 11) {
            return digits.replace(PHONE_REGEX_11, '$1-$2-$3')
        } else if (digits.length === 10) {
            return digits.replace(PHONE_REGEX_10, '$1-$2-$3')
        }
        return digits
    }, [])

    const getOrderDetail = async () => {
        try {
            const response = await addressApis.getOrderDetail({ uuid: uuid as string })
            setOrderDetail(response.data)
            setEditedPhone(response.data.ushop.detail_page.phone)
            setEditedEmail(response.data.ushop.detail_page.email)
            setPhoneFormat(formatPhoneNumber(response.data.ushop.detail_page.phone))
            setNoEmail(response.data.ushop.detail_page.email?.length === 0)
            setNoPhone(response.data.ushop.detail_page.phone?.length === 0)
            if (response.data.ushop.detail_page.email?.length === 0 || response.data.ushop.detail_page.phone?.length === 0) {
                setIsEdit(true)
            }
            setLoadingUuid(false)
        } catch (error: unknown) {
            console.log(error)
            setLoadingUuid(false)
        }
    }

    const handleEdit = useCallback(async () => {
        try {
            setError([])
            setEditContactError([])
            setLoading(true)

            const responseValidate = await addressApis.validatePhoneOrEmail({
                phone: phoneFormat.replace(/-/g, ''),
                email: editedEmail || ''
            })
            const uuid = new URLSearchParams(window.location.search).get('uuid')
            if (page === 'payment' && uuid) {
                const response = await addressApis.updateOrderDetail({
                    uuid: uuid as string,
                    email: editedEmail,
                    phone: phoneFormat.replace(/-/g, ''),
                    countryCode3Alpha: country.short
                })
                if (response.data.success === false) {
                    setEditContactError(response.data.Message_v2)
                } else {
                    const newOrderDetail = structuredClone(orderDetail)
                    newOrderDetail.ushop.detail_page.phone = phoneFormat.replace(/-/g, '')
                    newOrderDetail.ushop.detail_page.email = editedEmail
                    setEditedPhone(phoneFormat.replace(/-/g, ''))
                    setEditedEmail(editedEmail || '')
                    setDisplayEmail(editedEmail)
                    setPhoneFormat(phoneFormat)
                    setNoEmail(editedEmail?.length === 0)
                    setNoPhone(phoneFormat.replace(/-/g, '').length === 0)

                    setTrigger(new Date().getTime())
                    setOrderDetail(newOrderDetail)
                    setIsEdit(false)
                }
            } else {
                if (responseValidate.data.success === false) {
                    setEditContactError(responseValidate.data.Message_v2)
                } else {
                    setEditedPhone(phoneFormat.replace(/-/g, ''))
                    setEditedEmail(editedEmail || '')
                    setPhoneFormat(phoneFormat)
                    setDisplayEmail(editedEmail)
                    setNoEmail(editedEmail?.length === 0)
                    setNoPhone(phoneFormat.replace(/-/g, '').length === 0)
                    setIsEdit(false)
                    setTrigger(new Date().getTime())
                    const newOrderDetail = structuredClone(orderDetail)
                    newOrderDetail.ushop.detail_page.phone = phoneFormat.replace(/-/g, '')
                    newOrderDetail.ushop.detail_page.email = editedEmail || ''
                    setOrderDetail(newOrderDetail)
                    onCloseEdit()
                }
            }
        } catch (e) {
            console.log(e)
        } finally {
            setLoading(false)
        }
    }, [editedEmail, editedPhone, phoneFormat, country.short, orderDetail, uuid])

    const getFeelgreat = async () => {
        try {
            const response = await orderDetailsApis.getFeelgreat({
                countryCode3Alpha: country.short,
                userStatus: customerHelpers.customerStatus(customer) || 'C'
            })
            setFeelgreat(response.data)
        } catch (e) {
            console.log(e)
        }
    }
    // type IUuidItemCart = {
    //     item_code: string
    //     qty: number
    // }

    // const initWithAddressHistory = async () => {
    //     if (addressHistory.login_id.length > 0) {
    //         setError([])
    //         const items: IUuidItemCart[] = []
    //         products.forEach(product => {
    //             items.push({
    //                 item_code: product.item_code,
    //                 qty: (cart.find(c => c.product.item_code === product.item_code)?.quantity as number) || 1
    //             })
    //         })

    //         let address = {
    //             shipToAddress: {
    //                 address1: '',
    //                 address2: '',
    //                 city: '',
    //                 state: '',
    //                 zip: '',
    //                 country: ''
    //             },
    //             shipToName: {
    //                 firstName: '',
    //                 lastName: '',
    //                 fullName: '',
    //                 'fullName@ja': ''
    //             }
    //         }
    //         if (selectedAddressHistory.addressHref === 'main' || selectedAddressHistory.addressHref.length === 0) {
    //             address = addressHistory.mainAddress
    //         } else {
    //             const item = addressHistory.items.find(item => item.addressHref === selectedAddressHistory.addressHref)
    //             if (item) {
    //                 address = item
    //             }
    //         }

    //         const zip = address.shipToAddress.zip
    //         const shipToAddress = address.shipToAddress
    //         const email = editedEmail || customer?.email
    //         const phone = phoneFormat.replace(/-/g, '') || customerHelpers.customerPhone(customer)

    //         const postData = {
    //             fullname_jp: address.shipToName['fullName@ja'] as string,
    //             token: auth.token as string,
    //             ba_status: customerHelpers.customerStatus(customer) as string,
    //             email: email,
    //             login_id: customer?.id.unicity,
    //             login_name: customer?.humanName.fullName,
    //             login_name_native: customerHelpers.customerNameNative(customer) || '',
    //             period: period.entryPeriod,
    //             phone: phone.replace(/-/g, ''),
    //             agent: 'UNISHOP-WEB-JP',
    //             medium: 'Internet',
    //             platform: 'platform',
    //             send_confirmation: true,
    //             shipment_options: 'pickuptokyo',
    //             shipToAddress: shipToAddress,
    //             items: JSON.stringify(items),
    //             district: '',
    //             prefecture: '',
    //             town: '',
    //             house_number: '',
    //             zip: zip,
    //             selectAddressHistory: 'addressHistory'
    //         }

    //         try {
    //             setLoading(true)
    //             setLoadingInitAddressHistory(true)

    //             if (!customer?.email || !customerHelpers.customerPhone(customer)) {
    //                 setIsEdit(true)
    //                 setFormAddressField({
    //                     fullName: address.shipToName['fullName@ja'] as string,
    //                     zip: zip,
    //                     prefecture: shipToAddress.city,
    //                     district: shipToAddress.state,
    //                     townNumber: '',
    //                     houseNumber: ''
    //                 })
    //             } else {
    //                 const response = await addressApis.createUuid({ data: postData })
    //                 if (response.data.success) {
    //                     const ss = new URLSearchParams(window.location.search).get('ss')
    //                     navigate(`/payment?uuid=${response.data.uuid}${ss ? `&ss=${ss}` : ''}`)
    //                 } else {
    //                     let shouldCloseEdit = true
    //                     if (response.data.Message_v2.length > 0) {
    //                         const errorEditContact = []
    //                         if (response.data.Message_v2.includes('error_email_format')) {
    //                             setNoEmail(true)
    //                             shouldCloseEdit = false
    //                             errorEditContact.push(translate('error_email_format'))
    //                         }
    //                         if (response.data.Message_v2.includes('error_phone_length_10-11')) {
    //                             setNoPhone(true)
    //                             shouldCloseEdit = false
    //                             errorEditContact.push(translate('error_phone_length_10-11'))
    //                         }
    //                         if (errorEditContact.length > 0) {
    //                             // setEditContactError(errorEditContact)
    //                         } else {
    //                             setEditContactError([])
    //                             shouldCloseEdit = true
    //                         }
    //                         if (shouldCloseEdit) {
    //                             setIsEdit(false)
    //                         }

    //                         const formError: string[] = []
    //                         response.data.Message_v2.forEach((message: string) => {
    //                             if (!['error_email_format', 'error_phone_length_10-11'].includes(message)) {
    //                                 setNoEmail(true)
    //                                 shouldCloseEdit = false
    //                                 formError.push(translate(message))
    //                             }
    //                         })
    //                         if (formError.length > 0) {
    //                             setError(formError)
    //                         }
    //                     }

    //                     setOrderDetail({
    //                         ...orderDetail,
    //                         ushop: {
    //                             ...orderDetail.ushop,
    //                             detail_page: {
    //                                 ...orderDetail.ushop.detail_page,
    //                                 email: email,
    //                                 phone: phone.replace(/-/g, ''),
    //                                 shipToAddress: {
    //                                     address1: addressHistory.mainAddress.shipToAddress.address1,
    //                                     address2: addressHistory.mainAddress.shipToAddress.address2,
    //                                     city: addressHistory.mainAddress.shipToAddress.city,
    //                                     state: addressHistory.mainAddress.shipToAddress.state,
    //                                     zip: addressHistory.mainAddress.shipToAddress.zip,
    //                                     country: addressHistory.mainAddress.shipToAddress.country
    //                                 },
    //                                 zip: zip,
    //                                 prefecture: addressHistory.mainAddress.shipToAddress.city,
    //                                 district: addressHistory.mainAddress.shipToAddress.state
    //                             }
    //                         }
    //                     })
    //                 }
    //             }

    //             setLoading(false)
    //             setIsReady(true)
    //         } catch (error: unknown) {
    //             console.error(error)
    //             setError([translate('error_occurred')])
    //         } finally {
    //             setLoadingInitAddressHistory(false)
    //             setLoading(false)
    //         }
    //     }
    // }
    //Japan.Memberservice@unicity.com
    //0368004856
    const customerData = (key: string) => {
        if (page === 'payment' || editedEmail.length > 0 || editedPhone.length > 0) {
            if (key === 'email') {
                return displayEmail || orderDetail.ushop.detail_page.email || customer?.email
            } else if (key === 'phone') {
                return phoneFormat || formatPhoneNumber(editedPhone) || formatPhoneNumber(orderDetail.ushop.detail_page.phone)
            }
        } else {
            const data = {
                email: customer?.email,
                phone: formatPhoneNumber(customerHelpers.customerPhone(customer))
            }
            return data[key as keyof typeof data]
        }
    }

    const handleChange = (value: string) => {
        let input = value.replace(/\D/g, '') // keep only digits

        if (input.length > 11) {
            input = input.slice(0, 11) // limit to 11 digits for Japanese phone numbers
        }

        let formatted = input
        if (input.length > 6) {
            // For 7+ digits: 012-345-6789 or 012-345-67890
            if (input.length === 10) {
                // 10 digits: 012-345-6789 (3-3-4 format)
                formatted = `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6)}`
            } else {
                // 11 digits: 012-345-67890 (3-3-5 format)
                formatted = `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6)}`
            }
        } else if (input.length > 3) {
            // For 4-6 digits: 012-345
            formatted = `${input.slice(0, 3)}-${input.slice(3)}`
        } else if (input.length > 0) {
            // For 1-3 digits: 012
            formatted = input
        }

        setPhoneFormat(formatted)
        setNoPhone(input.length === 0)
    }

    const initialPhone = formatPhoneNumber(customerHelpers.customerPhone(customer))
    const initialEmail = customer?.email

    const canEditContact = () => {
        const forceEdit = noEmail || noPhone
        const isPaymentPage = page === 'payment'
        if (noEmail || noPhone) {
            return true
        }
        if (forceEdit) {
            return true
        } else {
            if (isPaymentPage) {
                return true
            } else {
                return false
            }
        }
    }

    // const initMainAddress = () => {
    //     setFormAddressField({
    //         fullName: addressHistory.mainAddress.shipToName['fullName@ja'] as string,
    //         zip: addressHistory.mainAddress.shipToAddress.zip,
    //         prefecture: addressHistory.mainAddress.shipToAddress.city,
    //         district: addressHistory.mainAddress.shipToAddress.state,
    //         townNumber: '',
    //         houseNumber: ''
    //     })
    // }

    const onCloseEdit = () => {
        setIsEdit(false)
        setEditedPhone(editedPhone || initialPhone)
        setEditedEmail(editedEmail || initialEmail)
        setEditContactError([])
        setError([])
        setTrigger(new Date().getTime())
    }

    useEffect(() => {
        if (uuid && !isReady) {
            setLoadingUuid(true)
        }
    }, [uuid, isReady])

    useEffect(() => {
        if (loadingUuid) {
            getOrderDetail()
        }
    }, [loadingUuid])

    const setToAddNewAddress = () => {
        if (page === 'payment') {
            navigate(`/${window.location.search}`)
        }
        setShouldAddNewAddress(true)
        setIsReady(true)
    }

    useEffect(() => {
        if (!isReady) {
            if (isProductCreditLoaded && isProductLoaded && isFetched) {
                const isAllowedAddressHistory = isAllowed && addressHistory.login_id.length > 0
                const isHasAddressHistory = addressHistory.mainAddress || addressHistory.items.length > 0

                if (isAllowedAddressHistory) {
                    if (isHasAddressHistory) {
                        getFeelgreat()
                        setIsReady(true)
                        navigate(`/payment${!isEmpty(window.location.search) ? `${window.location.search}` : ''}`)
                    } else {
                        setToAddNewAddress()
                    }
                } else {
                    setToAddNewAddress()
                }
            }
        }
    }, [isProductCreditLoaded, isProductLoaded, isReady, isFetched, addressHistory.login_id.length])

    // Auto-open edit dialog when email or phone is missing
    useEffect(() => {
        if (!isReady && (noEmail || noPhone)) {
            setIsEdit(true)
        }
    }, [noEmail, noPhone, isReady])

    useEffect(() => {
        if (editedEmail.length === 0 && editedPhone.length === 0 && language && !isEdit) {
            setEditedEmail(initialEmail)
            setEditedPhone(initialPhone)
            setPhoneFormat(formatPhoneNumber(initialPhone))
            setNoEmail(initialEmail?.length === 0)
            setNoPhone(initialPhone?.length === 0)
        }
    }, [editedEmail, editedPhone, language])

    return (
        <Stack>
            {isReady ? (
                <Stack
                    boxSizing={'border-box'}
                    padding={{
                        xs: '16px',
                        sm: '20px',
                        md: '40px 0px'
                    }}
                    gap={{
                        xs: '16px',
                        sm: '12px',
                        md: '12px',
                        lg: '24px'
                    }}
                    sx={{
                        flexDirection: {
                            xs: 'column',
                            md: 'row-reverse'
                        },
                        justifyContent: 'center',
                        alignItems: {
                            xs: 'center',
                            sm: 'center',
                            md: 'flex-start'
                        }
                    }}
                    width={'100%'}>
                    <Stack width={'100%'} sx={responsiveMainContainerSx}>
                        <Cart productCredits={productCredits} products={products} />
                        {/* <Stack
                            sx={{
                                '@media (max-width: 768px)': {
                                    display: 'none'
                                }
                            }}>
                            <MoneyBack />
                        </Stack> */}
                    </Stack>
                    <Stack gap={{ xs: '16px', sm: '12px', md: '12px' }} width={'100%'} sx={responsiveSecondaryContainerSx}>
                        <Card
                            sx={{
                                '&.MuiPaper-root': {
                                    boxSizing: 'border-box',
                                    padding: { xs: '16px', sm: '24px', md: '32px' }
                                }
                            }}>
                            <Stack mb={'16px'}>
                                <Typography variant="cardTitle" color={'#003B6F'}>
                                    {translate('account')}
                                </Typography>
                            </Stack>
                            <CardContent sx={{ padding: '0px!important' }}>
                                <Stack direction={'column'} gap={'8px'}>
                                    <Stack>
                                        <Typography variant="cardSubtitle" color={'#003B6F'}>
                                            {customer?.humanName.fullName}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Stack key={trigger}>
                                            <Typography variant="muted">{customerData('email')}</Typography>
                                            <Typography variant="muted">{customerData('phone')}</Typography>
                                        </Stack>
                                        {canEditContact() && (
                                            <Stack onClick={() => setIsEdit(true)}>
                                                <Typography
                                                    variant="body1"
                                                    color="#5A8FC3"
                                                    fontSize={'16px'}
                                                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                                    {translate('edit')}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                        {shouldAddNewAddress && page !== 'payment' ? (
                            <NewAddress orderDetail={orderDetail} getAddressHistory={getAddressHistory} parentError={error} setParentError={setError} />
                        ) : (
                            <Payment productCredits={productCredits} />
                        )}
                    </Stack>
                    <Dialog
                        open={isEdit}
                        onClose={() => {
                            if (editedPhone.length > 0 && editedEmail.length > 0) {
                                onCloseEdit()
                            }
                        }}
                        disableEscapeKeyDown
                        fullWidth
                        sx={{
                            '& .MuiDialog-paper': {
                                padding: '32px',
                                borderRadius: '12px',
                                gap: '32px',
                                width: {
                                    xs: '343px',
                                    sm: '600px'
                                }
                            }
                        }}>
                        <DialogTitle sx={{ padding: 0 }}>
                            <Typography variant="cardTitle" color={'#003B6F'}>
                                {translate('contact')}
                            </Typography>
                        </DialogTitle>
                        <DialogContent sx={{ padding: '0px', gap: '32px', display: 'flex', flexDirection: 'column' }}>
                            <Stack
                                display={{
                                    xs: 'flex',
                                    sm: 'grid'
                                }}
                                gridTemplateColumns={'1fr 1fr'}
                                gap={'16px'}>
                                <InputBox
                                    label={translate('phone')}
                                    value={phoneFormat}
                                    onChange={(value: string) => {
                                        if (value.length === 0) {
                                            setEditedPhone('')
                                            setPhoneFormat('')
                                            setNoPhone(true)
                                        } else {
                                            handleChange(value)
                                        }
                                    }}
                                    type="tel"
                                />
                                <InputBox
                                    label={translate('email')}
                                    value={editedEmail}
                                    onChange={(value: string) => {
                                        if (value.length === 0) {
                                            setEditedEmail('')
                                            setNoEmail(true)
                                        } else {
                                            setEditedEmail(value)
                                            setNoEmail(value.length === 0)
                                        }
                                    }}
                                    type="email"
                                />
                            </Stack>
                            {editContactError.length > 0 && <ErrorBox messages={editContactError} />}
                            <Stack justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ width: '170px', textTransform: 'none' }}
                                    onClick={() => {
                                        handleEdit()
                                    }}
                                    disableElevation
                                    loading={loading}
                                    disabled={loading}
                                    loadingPosition="center">
                                    {translate('save_contact')}
                                </Button>
                                {!forceEnterRequiredData && (
                                    <Box onClick={onCloseEdit} sx={{ cursor: 'pointer' }}>
                                        <Typography variant="link" color={'#003B6F'}>
                                            {translate('cancel')}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </DialogContent>
                    </Dialog>
                </Stack>
            ) : (
                <Stack
                    boxSizing={'border-box'}
                    padding={{
                        xs: '16px',
                        sm: '20px',
                        md: '40px 0px'
                    }}
                    gap={{
                        xs: '16px',
                        sm: '12px',
                        md: '12px',
                        lg: '24px'
                    }}
                    sx={{
                        flexDirection: {
                            xs: 'column',
                            md: 'row-reverse'
                        },
                        justifyContent: 'center',
                        alignItems: {
                            xs: 'center',
                            sm: 'center',
                            md: 'flex-start'
                        }
                    }}
                    width={'100%'}>
                    <Stack width={'100%'} sx={responsiveMainContainerSx}>
                        <Skeleton variant="rounded" sx={responsiveSkeletonMainSx} />
                    </Stack>
                    <Stack gap={{ xs: '16px', sm: '12px', md: '12px' }} width={'100%'} sx={responsiveSecondaryContainerSx}>
                        <Skeleton variant="rounded" sx={{ ...responsiveSkeletonSecondarySx, height: '180px' }} />
                        <Skeleton variant="rounded" sx={{ ...responsiveSkeletonSecondarySx, height: '500px' }} />
                    </Stack>
                </Stack>
            )}
        </Stack>
    )
}

export default OrderDetails
